import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ImageUploaderProps {
  routineId: string;
}

export const ImageUploader = ({ routineId }: ImageUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor, sube solo archivos de imagen.",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "La imagen es demasiado grande. El tamaño máximo es 5MB.",
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('routineId', routineId);

      const { data, error } = await supabase.functions.invoke('upload-routine-image', {
        body: formData,
      });

      if (error) throw error;

      toast({
        title: "Imagen subida",
        description: "La imagen se ha subido correctamente.",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo subir la imagen. Por favor, intenta de nuevo.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <input
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        id={`image-upload-${routineId}`}
        disabled={isUploading}
      />
      <Button
        variant="outline"
        size="icon"
        className="dark:border-white/20 bg-primary dark:bg-white dark:hover:text-black dark:hover:bg-gray-300 dark:text-black text-white 
        ocean:bg-[#081F2B] ocean:hover:text-white sunset:bg-[#f26018] forest:bg-forest/90 forest:text-white"
        disabled={isUploading}
        onClick={() => {
          const fileInput = document.getElementById(`image-upload-${routineId}`);
          if (fileInput) {
            fileInput.click();
          }
        }}
      >
        <Upload className="h-4 w-4" />
      </Button>
    </>
  );
};